import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import { formatDateOnly } from '../utils/formatDateOnly';
import type {
  EhrChangeDecision,
  EhrChangeDecisionValue,
  EhrChangeItem,
  EhrChangeKind,
} from '../utils/ehrChanges';

/**
 * PHA-1141: the EHR changes to a patient's problem or medication list, reviewed in place.
 *
 * Mirrors the onboarding review (ApprovalWorkflow): each change is accepted or rejected on its
 * own, or all at once, and the clinician can correct the EHR's version before accepting it.
 * Accepting writes the change into the chart's list, stamped with who acknowledged it and
 * when; rejecting leaves the list as it is. Either way the decision is recorded and the change
 * leaves the Smart Hub. Undecided rows stay pending.
 */
export interface EhrChangesReviewPanelProps {
  documentType: 'problem_list' | 'medication_list';
  items: EhrChangeItem[];
  onSubmit: (decisions: EhrChangeDecision[]) => Promise<void>;
  /** Draw attention when the chart was opened from the Smart Hub for these changes. */
  highlighted?: boolean;
}

const KIND_CHIPS: Record<EhrChangeKind, { label: string; color: 'primary' | 'success' | 'warning' | 'error' | 'default' }> = {
  problem_added: { label: 'NEW', color: 'primary' },
  problem_resolved: { label: 'RESOLVED', color: 'success' },
  med_added: { label: 'NEW', color: 'primary' },
  med_stopped: { label: 'STOPPED', color: 'warning' },
  med_dosage_changed: { label: 'DOSE CHANGED', color: 'warning' },
  med_removed: { label: 'REMOVED FROM EHR', color: 'error' },
  observation: { label: 'NEW', color: 'primary' },
};

const WHAT_ACCEPT_DOES: Record<EhrChangeKind, string> = {
  problem_added: 'Accept adds it to the problem list.',
  problem_resolved: 'Accept keeps it on the list, marked with the EHR status.',
  med_added: 'Accept adds it to the medication list.',
  med_stopped: 'Accept keeps it on the list with the EHR end date.',
  med_dosage_changed: 'Accept updates the dose on the list.',
  med_removed: 'Accept removes it from the medication list.',
  observation: '',
};

/** Fields the clinician may correct before accepting; a removal or resolution is taken as is. */
const EDITABLE: Partial<Record<EhrChangeKind, string[]>> = {
  problem_added: ['problem', 'icd10_code', 'onset_date'],
  med_added: ['medication', 'dosage', 'start_date'],
  med_dosage_changed: ['dosage'],
  med_stopped: ['end_date'],
};

const FIELD_LABELS: Record<string, string> = {
  problem: 'Problem',
  icd10_code: 'ICD-10',
  onset_date: 'Onset',
  medication: 'Medication',
  dosage: 'Dose',
  start_date: 'Start',
  end_date: 'End',
};

const DATE_FIELDS = new Set(['onset_date', 'start_date', 'end_date']);

const keyOf = (item: EhrChangeItem) => `${item.change_kind}|${item.item_key}|${item.ehr_sync_run_id}`;

const itemName = (item: EhrChangeItem, documentType: string) =>
  (documentType === 'problem_list' ? item.record?.problem : item.record?.medication) || item.label || '—';

const EhrChangesReviewPanel: React.FC<EhrChangesReviewPanelProps> = ({
  documentType,
  items,
  onSubmit,
  highlighted = false,
}) => {
  const [decisions, setDecisions] = useState<Record<string, EhrChangeDecisionValue | undefined>>({});
  const [edits, setEdits] = useState<Record<string, Record<string, any>>>({});
  const [saving, setSaving] = useState(false);

  // Items can disappear underneath (another clinician decided them): drop stale local state.
  useEffect(() => {
    const live = new Set(items.map(keyOf));
    setDecisions((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => live.has(k))));
    setEdits((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => live.has(k))));
  }, [items]);

  const decided = useMemo(() => items.filter((i) => decisions[keyOf(i)]), [items, decisions]);

  if (items.length === 0) return null;

  const listName = documentType === 'problem_list' ? 'problem list' : 'medication list';
  const setAll = (value: EhrChangeDecisionValue) =>
    setDecisions(Object.fromEntries(items.map((i) => [keyOf(i), value])));

  const toggle = (item: EhrChangeItem, value: EhrChangeDecisionValue) =>
    setDecisions((prev) => ({ ...prev, [keyOf(item)]: prev[keyOf(item)] === value ? undefined : value }));

  const fieldValue = (item: EhrChangeItem, field: string) => {
    const edited = edits[keyOf(item)];
    if (edited && field in edited) return edited[field] ?? '';
    return item.record?.[field] ?? '';
  };

  const setField = (item: EhrChangeItem, field: string, value: string) =>
    setEdits((prev) => ({ ...prev, [keyOf(item)]: { ...(prev[keyOf(item)] || {}), [field]: value } }));

  const save = async () => {
    const payload: EhrChangeDecision[] = decided.map((item) => {
      const decision = decisions[keyOf(item)] as EhrChangeDecisionValue;
      const changed = edits[keyOf(item)];
      const out: EhrChangeDecision = {
        change_kind: item.change_kind,
        item_key: item.item_key,
        ehr_sync_run_id: item.ehr_sync_run_id,
        decision,
      };
      if (decision === 'accepted' && changed && Object.keys(changed).length > 0) {
        // PHA-955: a cleared date input is '' — send null, never ''.
        out.edits = Object.fromEntries(
          Object.entries(changed).map(([k, v]) => [k, DATE_FIELDS.has(k) && !String(v ?? '').trim() ? null : v]),
        );
      }
      return out;
    });
    setSaving(true);
    try {
      await onSubmit(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        borderColor: highlighted ? 'warning.main' : 'divider',
        borderWidth: highlighted ? 2 : 1,
        bgcolor: '#fffdf5',
      }}
      data-testid={`ehr-changes-review-${documentType}`}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            EHR changes to review ({items.length})
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Found by the EHR sync. Accept a change to apply it to the {listName}, or reject it to keep
            the {listName} as it is. Changes you leave undecided stay pending.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" color="success" onClick={() => setAll('accepted')} disabled={saving}>
            Accept all
          </Button>
          <Button size="small" variant="outlined" color="error" onClick={() => setAll('rejected')} disabled={saving}>
            Reject all
          </Button>
          <Button size="small" variant="contained" onClick={save} disabled={saving || decided.length === 0}>
            {saving ? 'Saving…' : `Acknowledge ${decided.length > 0 ? `(${decided.length})` : ''}`}
          </Button>
        </Box>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 150 }}>Change</TableCell>
            <TableCell>{documentType === 'problem_list' ? 'Problem' : 'Medication'}</TableCell>
            <TableCell sx={{ width: 110 }}>Date of change</TableCell>
            <TableCell sx={{ width: 110 }} align="center">
              Accept / Reject
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => {
            const k = keyOf(item);
            const decision = decisions[k];
            const chip = KIND_CHIPS[item.change_kind];
            const editable = EDITABLE[item.change_kind] || [];
            const changeDate =
              (item.change_kind === 'med_stopped' && item.record?.end_date) || item.detected_at || null;
            return (
              <TableRow
                key={k}
                sx={{
                  bgcolor: decision === 'accepted' ? '#e8f5e9' : decision === 'rejected' ? '#fdecea' : 'inherit',
                }}
              >
                <TableCell>
                  <MuiTooltip title={WHAT_ACCEPT_DOES[item.change_kind]} placement="top">
                    <Chip label={chip.label} size="small" color={chip.color} />
                  </MuiTooltip>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: editable.length ? 0.5 : 0 }}>
                    {itemName(item, documentType)}
                    {documentType === 'problem_list' && item.record?.icd10_code ? ` (${item.record.icd10_code})` : ''}
                  </Typography>
                  {item.change_kind === 'med_dosage_changed' && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Dose {item.previous_dosage || '—'} → {item.record?.dosage || '—'}
                    </Typography>
                  )}
                  {item.change_kind === 'problem_resolved' && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      EHR status: {item.record?.clinical_status || 'resolved'}
                    </Typography>
                  )}
                  {editable.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                      {editable.map((field) => (
                        <TextField
                          key={field}
                          size="small"
                          label={FIELD_LABELS[field] || field}
                          type={DATE_FIELDS.has(field) ? 'date' : 'text'}
                          value={String(fieldValue(item, field) ?? '').slice(0, DATE_FIELDS.has(field) ? 10 : undefined)}
                          onChange={(e) => setField(item, field, e.target.value)}
                          InputLabelProps={DATE_FIELDS.has(field) ? { shrink: true } : undefined}
                          sx={{ minWidth: field === 'problem' || field === 'medication' ? 240 : 130 }}
                          disabled={saving}
                        />
                      ))}
                    </Box>
                  )}
                </TableCell>
                <TableCell>{formatDateOnly(changeDate)}</TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    aria-label="Accept change"
                    color={decision === 'accepted' ? 'success' : 'default'}
                    onClick={() => toggle(item, 'accepted')}
                    disabled={saving}
                  >
                    <Check fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="Reject change"
                    color={decision === 'rejected' ? 'error' : 'default'}
                    onClick={() => toggle(item, 'rejected')}
                    disabled={saving}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default EhrChangesReviewPanel;
